import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { create } from 'zustand';
import { uploadBook } from '../lib/queries';

/** A book added with others, and the track it was added from. */
export interface Added {
  id: string;
  /** Its file's name: the book has no other until the file is read. */
  name: string;
  onto?: string;
}

/** A file that didn't go, and why. */
export interface Missed {
  name: string;
  error: string;
}

interface UploadState {
  /** The file going up now, and how far: 0 to 1. */
  name: string | null;
  progress: number;
  /** The files chosen after it, waiting their turn. */
  queue: { file: File; onto?: string }[];
  /** How many files this run is of, and how many of them are done with, gone up or not. */
  total: number;
  sent: number;
  /**
   * What adding several at once brought, until the reader puts it away: the
   * books, which wait for their track, and the files that didn't go.
   */
  added: Added[];
  missed: Missed[];
  /** Why the one file chosen didn't go. */
  error: string | null;
  /** The track each book was added from, by its id, for this visit: its question of which track starts there. */
  from: Record<string, string>;
  /**
   * Uploads books (EPUBs and PDFs), one after another, and resolves to the
   * book's id when one file was chosen and it went. Files chosen while others
   * go up join them.
   */
  start: (files: readonly File[], onto?: string) => Promise<string | null>;
  clear: () => void;
  /** Puts away what adding several at once brought. */
  dismiss: () => void;
}

const BOOK_FILE = /\.(epub|pdf)$/i;
const NOT_A_BOOK = 'That isn’t an EPUB or a PDF. Only .epub and .pdf files can be added.';

/**
 * The uploads, shared by the library, the drop zone and the page for adding
 * a book: one file at a time, in the order chosen, so a pile of them is
 * gentle on the server. One that doesn't go doesn't stop the rest.
 */
export const useUpload = create<UploadState>((set, get) => {
  let running = false;
  const run = async (): Promise<string | null> => {
    running = true;
    const added: Added[] = [];
    const missed: Missed[] = [];
    for (let next = get().queue[0]; next; next = get().queue[0]) {
      const { file, onto } = next;
      set((s) => ({ queue: s.queue.slice(1) }));
      if (!BOOK_FILE.test(file.name)) {
        missed.push({ name: file.name, error: NOT_A_BOOK });
      } else {
        set({ name: file.name, progress: 0 });
        try {
          const book = await uploadBook(file, (progress) => set({ progress }));
          added.push({ id: book.id, name: file.name, onto });
          if (onto) set((s) => ({ from: { ...s.from, [book.id]: onto } }));
        } catch (e) {
          missed.push({ name: file.name, error: e instanceof Error ? e.message : String(e) });
        }
      }
      set((s) => ({ sent: s.sent + 1 }));
    }
    running = false;
    const one = get().total === 1;
    set({ name: null, progress: 0, total: 0, sent: 0 });
    // One file, as ever: its own page next, or why it didn't go.
    if (one) {
      if (missed[0]) set({ error: missed[0].error });
      return added[0]?.id ?? null;
    }
    set((s) => ({ added: [...s.added, ...added], missed: [...s.missed, ...missed] }));
    return null;
  };
  return {
    name: null,
    progress: 0,
    queue: [],
    total: 0,
    sent: 0,
    added: [],
    missed: [],
    error: null,
    from: {},
    start: (files, onto) => {
      if (!files.length) return Promise.resolve(null);
      set((s) => ({
        queue: [...s.queue, ...files.map((file) => ({ file, onto }))],
        total: s.total + files.length,
        error: null,
      }));
      return running ? Promise.resolve(null) : run();
    },
    clear: () => set({ error: null }),
    dismiss: () => set({ added: [], missed: [] }),
  };
});

/** Opens the system file picker for books: EPUBs and PDFs, one or several. */
export function pickBooks(): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.epub,.pdf,application/epub+zip,application/pdf';
    input.onchange = () => resolve(Array.from(input.files ?? []));
    input.oncancel = () => resolve([]);
    input.click();
  });
}

/**
 * Uploads books. One goes to its page, where it is analysed; added from a
 * track, the question of which track starts on that one. Several stay where
 * the reader is, and the head of the page lists them once they are up.
 */
export function useUploadTo() {
  const start = useUpload((s) => s.start);
  const navigate = useNavigate();
  return useCallback(
    async (files: readonly File[], track?: string) => {
      const id = await start(files, track);
      if (!id) return;
      await navigate({ to: '/books/$bookId', params: { bookId: id }, search: track ? { onto: track } : {} });
    },
    [start, navigate],
  );
}

/** Opens the picker for books and uploads them: for a track, when the reader adds them from one. */
export function useImport(track?: string) {
  const upload = useUploadTo();
  return () => void pickBooks().then((files) => upload(files, track));
}
